// API Client for Void Match
// Handles authentication, loadout persistence, and other API calls

export interface User {
    id: string;
    provider: string;
    providerId: string;
    displayName: string;
    avatarUrl: string | null;
}

export interface FittedModule {
    slotIndex: number;
    slotType: 'weapon' | 'support';
    module: {
        id: string;
        definitionId: string;
        level: number;
    } | null;
}

export interface Loadout {
    id: number;
    slotNumber: number;
    name: string;
    data: {
        fittedModules: FittedModule[];
    };
}

class ApiClient {
    private baseUrl: string;

    constructor() {
        // In production, API is on same domain
        // In development, might be different port
        this.baseUrl = '/api';
    }

    private async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
        const response = await fetch(`${this.baseUrl}${path}`, {
            ...options,
            credentials: 'include', // Include cookies for session
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(error.error || 'Request failed');
        }

        return response.json();
    }

    // ==================== AUTH ====================

    async getMe(): Promise<User | null> {
        const result = await this.fetch<{ user: User | null }>('/auth/me');
        return result.user;
    }

    async logout(): Promise<void> {
        await this.fetch('/auth/logout', { method: 'POST' });
    }

    async updateDisplayName(displayName: string): Promise<{ success: boolean; displayName: string }> {
        return this.fetch('/auth/me', {
            method: 'PATCH',
            body: JSON.stringify({ displayName }),
        });
    }

    // Get OAuth URLs (redirect user to these)
    getGoogleAuthUrl(): string {
        return `${this.baseUrl}/auth/google`;
    }

    getDiscordAuthUrl(): string {
        return `${this.baseUrl}/auth/discord`;
    }

    // ==================== LOADOUTS ====================

    async getLoadout(): Promise<Loadout | null> {
        const result = await this.fetch<{ loadout: Loadout | null }>('/player/loadout');
        return result.loadout;
    }

    async saveLoadout(fittedModules: FittedModule[]): Promise<void> {
        await this.fetch('/player/loadout', {
            method: 'POST',
            body: JSON.stringify({ fittedModules }),
        });
    }

    async getAllLoadouts(): Promise<Loadout[]> {
        const result = await this.fetch<{ loadouts: Loadout[] }>('/player/loadouts');
        return result.loadouts;
    }

    async activateLoadout(slotNumber: number): Promise<void> {
        await this.fetch(`/player/loadouts/${slotNumber}/activate`, {
            method: 'POST',
        });
    }

    // ==================== INVENTORY / SCRAP / BLUEPRINTS ====================

    async getInventory(): Promise<{ scrap: number; weaponBlueprints: number; supportBlueprints: number; threatLevel: number; modules: any[] }> {
        const result = await this.fetch<{ inventory: { scrap: number; weaponBlueprints: number; supportBlueprints: number; threatLevel: number; modules: any[] } }>('/player/inventory');
        return result.inventory;
    }

    async updateScrap(amount: number): Promise<{ success: boolean; scrap: number }> {
        return this.fetch('/player/scrap', {
            method: 'POST',
            body: JSON.stringify({ amount }),
        });
    }

    async updateBlueprints(weaponAmount?: number, supportAmount?: number): Promise<{ success: boolean; weaponBlueprints: number; supportBlueprints: number }> {
        return this.fetch('/player/blueprints', {
            method: 'POST',
            body: JSON.stringify({ weaponAmount, supportAmount }),
        });
    }

    async saveInventory(modules: Array<{ definitionId: string; instanceId: string; level: number }>): Promise<void> {
        await this.fetch('/player/inventory', {
            method: 'POST',
            body: JSON.stringify({ modules }),
        });
    }

    async startRun(threatLevel: number): Promise<{ success: boolean; scrap: number; threatLevel: number; costDeducted: number }> {
        return this.fetch('/player/start-run', {
            method: 'POST',
            body: JSON.stringify({ threatLevel }),
        });
    }
}

// Singleton instance
export const apiClient = new ApiClient();
